import { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../lib/apiConfig';
import { useAuth } from '../hooks/useAuth';
import ConfirmDialog from '../components/ConfirmDialog';
import Modal from '../components/Modal';
import FamilyMemberForm from '../components/FamilyMemberForm';
import { useToast } from '../components/context/ToastContext';
import type { FamilyMember, FamilyMemberFormData } from '../types/family';
import { calculateAge, isMinorForHipaa } from '../utils/stateConsentAges';
import {
  FileText,
  Info,
  Lock,
  ArrowLeft,
  X,
  UserCheck,
  Baby,
  Heart,
  UserPlus,
  ChevronDown,
  ChevronUp,
  Paperclip,
  Camera,
  Plus,
  UploadCloud,
  X as XIcon,
} from 'lucide-react';
import ExampleBillModal from '../components/ExampleBillModal';
import { showIntercom, trackBillUploaded, trackPaymentCompleted, updateIntercom } from '../lib/intercom';
import { addonPriceFor, startAddonCheckout, verifyAddonPayment } from '../lib/addonCheckout';
import { phBillSubmitted, phFailure, phFreeBillUploaded } from '../lib/posthog';
import StartCasePaywall from '../components/StartCasePaywall';
import {
  clearPaidDraftId,
  deleteBillDraft,
  loadBillDraft,
  paidDraftId,
  saveBillDraft,
  type BillDraft,
} from '../lib/freeAccount';
import { shrinkImage } from '../lib/shrinkImage';
import { prepareImageForUpload } from '../lib/imageUpload';
import { refreshPostHogIdentity } from '../components/PostHogIdentifier';
import { trackCustomEvent } from '../lib/fbpixel';
import '@styles/pages/add-bill.css';
import '@styles/pages/family.css';
import '@styles/components/start-case-paywall.css';

type Subscription = {
  bills_limit: number;
  /* Kept up to date by a trigger on bills, and reset when an annual plan
     renews, so it's the count for the current plan year. */
  bills_used?: number | null;
  plan?: string | null;
  status: 'active' | string;
  /* Short_id added by migration 044. Optional because rows from before
     the backfill (or environments where 044 hasn't been applied yet)
     don't have it. Used in mailto pre-fills so support inbox can find
     the customer's record without a back-and-forth. */
  short_id?: string | null;
};

/* iPhone photos are HEIC, and an iPhone photo AirDropped to a Mac often
   reaches Chrome as HEIC or with no type at all. We used to turn those
   away with "PDF, JPG or PNG only" (J-C, 12 Oct 2026). Now they're taken,
   converted to JPEG where the browser can, and the server converts the
   rest before reading. */
const ACCEPTED_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
];
const ACCEPTED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
/** For the file pickers. image/* lets phones offer the photo library. */
const ACCEPT_ATTR = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/*';

function isAcceptedFile(f: File): boolean {
  if (ACCEPTED_TYPES.includes(f.type.toLowerCase())) return true;
  const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
  // Type is empty or something odd like application/octet-stream: go by the name.
  return ACCEPTED_EXTENSIONS.includes(ext) && (!f.type || f.type === 'application/octet-stream' || f.type.startsWith('image/'));
}

function fileExt(f: File): string {
  return f.name.split('.').pop()?.toLowerCase().slice(0, 8) ?? '';
}
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
/** Extra pages a free account can add to one bill. */
const MAX_EXTRA_PAGES = 5;

export default function AddBill() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [providerName, setProviderName] = useState('');
  const [comment, setComment] = useState('');
  const [isForFamilyMember, setIsForFamilyMember] = useState(false);

  /* Scroll target for the "attach a bill first" nudge (the upload card). */
  const uploadRef = useRef<HTMLDivElement>(null);
  /* Phones and tablets. "Drop your bill here" means nothing on a phone,
     and a camera button means nothing on a laptop. */
  const [isTouch] = useState(() => {
    try {
      return window.matchMedia('(pointer: coarse)').matches;
    } catch {
      return false;
    }
  });

  // EOB optional attachment
  const [showEobUpload, setShowEobUpload] = useState(false);
  const [eobFile, setEobFile] = useState<File | null>(null);
  const [eobUpgradePath, setEobUpgradePath] = useState<string | null>(null);
  const [isDraggingEob, setIsDraggingEob] = useState(false);
  const eobInputRef = useRef<HTMLInputElement>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [billsUsed, setBillsUsed] = useState<number>(0);
  const [pageLoading, setPageLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  /* Free accounts (2026-10-05). isFree = signed in, no plan. They can
     upload bills, each parked as a draft until they pay. Since migration
     115 there can be several, and every upload here adds a new one. A paid
     account can have a draft too, left over from before they paid, and
     this page then offers it ready to send: the one they paid from if we
     know it, otherwise their newest. */
  const [isFree, setIsFree] = useState(false);
  const [draft, setDraft] = useState<BillDraft | null>(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [addonLoading, setAddonLoading] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const [paywallTrigger, setPaywallTrigger] = useState('first_look');
  // More pages of the same bill, and when it's due. Free upload only.
  const [extraFiles, setExtraFiles] = useState<File[]>([]);
  const [dueDate, setDueDate] = useState('');
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const extraInputRef = useRef<HTMLInputElement>(null);

  // HIPAA state
  const [showHipaaPrompt, setShowHipaaPrompt] = useState(false);
  const [hasSelfHipaa, setHasSelfHipaa] = useState(false);

  // Family member state
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [selectedFamilyMember, setSelectedFamilyMember] = useState<FamilyMember | null>(null);
  const [hipaaStatus, setHipaaStatus] = useState<
    Record<string, { signed: boolean; signerRelationship: string | null }>
  >({});

  // Popup flow state
  const [showFamilyPickerPopup, setShowFamilyPickerPopup] = useState(false);
  const [showAddFamilyPopup, setShowAddFamilyPopup] = useState(false);
  const [addFamilyLoading, setAddFamilyLoading] = useState(false);
  const [addFamilyError, setAddFamilyError] = useState<string | null>(null);
  const [showFamilyHipaaPrompt, setShowFamilyHipaaPrompt] = useState<FamilyMember | null>(null);

  const hasUnsavedChanges =
    file !== null ||
    providerName.trim() !== '' ||
    comment.trim() !== '' ||
    isForFamilyMember;

  /* ---------------------------
     AUTH + SUBSCRIPTION + HIPAA LOAD
  ---------------------------- */
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }

    const load = async () => {
      // No plan yet? Then this is a free account: no HIPAA form, no bill
      // allowance, just the upload and whatever they already parked.
      const { data: anySub } = await supabase
        .from('subscriptions')
        .select('id')
        .eq('user_id', user.id)
        .limit(1);

      if (!anySub || anySub.length === 0) {
        // A free account that already uploaded belongs on its case page,
        // unless they came here to add another bill (?new=1).
        const addingAnother = new URLSearchParams(window.location.search).get('new') === '1';
        if (!addingAnother) {
          const parked = await loadBillDraft(user.id);
          if (parked) {
            navigate(`/case/draft?id=${parked.id}`, { replace: true });
            return;
          }
        }
        setIsFree(true);
        setPageLoading(false);
        return;
      }

      const existingDraft = await loadBillDraft(user.id, paidDraftId());
      setDraft(existingDraft);

      /* Back from paying for another bill: confirm the payment first, so the
         limit below already includes the new bill and the form opens. */
      const params = new URLSearchParams(window.location.search);
      const addonSessionId = params.get('addon') === 'success' ? params.get('session_id') : null;
      if (addonSessionId) {
        window.history.replaceState({}, '', '/add-bill');
        const paid = await verifyAddonPayment(addonSessionId);
        if (paid) {
          trackPaymentCompleted({ plan: 'addon_bill', amount: addonPriceFor(paid.plan) });
          refreshPostHogIdentity();
          showToast("Payment done. Send us the bill and we'll get started.", 'success');
        } else {
          showToast('Payment received. If the form doesn’t open, refresh in a minute.', 'info');
        }
      }

      if (existingDraft) {
        setProviderName(existingDraft.provider_name ?? '');
        setComment(existingDraft.comment ?? '');
      }

      // Check if user has signed HIPAA for themselves
      const { data: hipaa } = await supabase
        .from('hipaa_consents')
        .select('id')
        .eq('user_id', user.id)
        .is('family_member_id', null)
        .limit(1)
        .maybeSingle();

      if (!hipaa) {
        setHasSelfHipaa(false);
        setShowHipaaPrompt(true);
        setPageLoading(false);
        return;
      }

      setHasSelfHipaa(true);

      // Load subscription (incl. short_id from migration 044 for mailto links)
      const { data: sub } = await supabase
        .from('subscriptions')
        .select('bills_limit, bills_used, plan, status, short_id')
        .eq('user_id', user.id)
        .single();

      setSubscription(sub ?? null);

      /* Use bills_used rather than counting every bill they've ever sent.
         Counting all bills meant an annual customer stayed locked out
         after renewing. Fall back to the count if the column is empty. */
      if (typeof sub?.bills_used === 'number') {
        setBillsUsed(sub.bills_used);
      } else {
        const { count } = await supabase
          .from('bills')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id);
        setBillsUsed(count ?? 0);
      }

      // Load family members
      const { data: members } = await supabase
        .from('family_members')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      const familyData = members ?? [];
      setFamilyMembers(familyData);

      // Check HIPAA status for each family member
      if (familyData.length > 0) {
        const memberIds = familyData.map((m) => m.id);
        const { data: consents } = await supabase
          .from('hipaa_consents')
          .select('family_member_id, signer_relationship')
          .eq('user_id', user.id)
          .in('family_member_id', memberIds);

        const statusMap: Record<string, { signed: boolean; signerRelationship: string | null }> = {};
        for (const m of familyData) {
          const consent = consents?.find((c) => c.family_member_id === m.id);
          statusMap[m.id] = {
            signed: !!consent,
            signerRelationship: consent?.signer_relationship ?? null,
          };
        }
        setHipaaStatus(statusMap);
      }

      // Check if we're returning from an EOB-upgrade Stripe checkout, carry over the EOB file
      const savedEobPath = localStorage.getItem('eob_upgrade_path');
      if (savedEobPath) {
        setEobUpgradePath(savedEobPath);
        setShowEobUpload(true);
      }

      setPageLoading(false);
    };

    load();
  }, [authLoading, user, navigate, showToast]);

  const outOfBills =
    subscription &&
    subscription.status === 'active' &&
    billsUsed >= subscription.bills_limit;

  const isAnnualPlan = subscription?.plan === 'annual';
  const addonPrice = addonPriceFor(subscription?.plan);

  // Always Home. Going "back" could land on the login or Stripe page they
  // came through, which looks like being logged out.
  const leaveLimitCard = () => navigate('/', { replace: true });

  const handleBuyAnotherBill = async () => {
    if (!user || addonLoading) return;
    setAddonLoading(true);
    const err = await startAddonCheckout({
      plan: subscription?.plan,
      email: user.email,
      userId: user.id,
      returnTo: '/add-bill',
    });
    if (err) {
      showToast(err, 'error');
      setAddonLoading(false);
    }
  };

  /* ---------------------------
     HIPAA PROMPT ACTIONS (no self-HIPAA)
  ---------------------------- */
  const handleHipaaForMe = () => {
    navigate('/hipaa?returnTo=/add-bill');
  };

  const handleHipaaForFamily = () => {
    setShowHipaaPrompt(false);
    setShowFamilyPickerPopup(true);
  };

  /* ---------------------------
     FAMILY MEMBER CHECKBOX (has self-HIPAA)
  ---------------------------- */
  const handleFamilyCheckboxChange = (checked: boolean) => {
    setIsForFamilyMember(checked);
    if (checked) {
      setShowFamilyPickerPopup(true);
    } else {
      setSelectedFamilyMember(null);
    }
  };

  /* ---------------------------
     FAMILY PICKER POPUP
  ---------------------------- */
  const handleSelectFamilyMember = (member: FamilyMember) => {
    setSelectedFamilyMember(member);
    setShowFamilyPickerPopup(false);

    const status = hipaaStatus[member.id];
    const hasSigned = status?.signed ?? false;

    // Check if a child has aged out, parent-signed HIPAA is invalid once they turn 18
    const agedOut =
      hasSigned &&
      status?.signerRelationship === 'parent' &&
      !isMinorForHipaa(member.date_of_birth);

    if (!hasSigned || agedOut) {
      setShowFamilyHipaaPrompt(member);
    }
  };

  const handleAddFamilyMember = async (data: FamilyMemberFormData) => {
    if (!user) return;
    setAddFamilyLoading(true);
    setAddFamilyError(null);

    // Check for duplicate (same name + DOB)
    const { data: dup } = await supabase
      .from('family_members')
      .select('id')
      .eq('user_id', user.id)
      .ilike('first_name', data.first_name)
      .ilike('last_name', data.last_name)
      .eq('date_of_birth', data.date_of_birth)
      .maybeSingle();

    if (dup) {
      setAddFamilyError('A family member with this name and date of birth already exists.');
      setAddFamilyLoading(false);
      return;
    }

    const { data: newMember, error: insertErr } = await supabase
      .from('family_members')
      .insert({ user_id: user.id, ...data })
      .select()
      .single();

    if (insertErr || !newMember) {
      setAddFamilyError(insertErr?.message ?? 'Failed to add family member');
      setAddFamilyLoading(false);
      return;
    }

    setFamilyMembers((prev) => [...prev, newMember]);
    setHipaaStatus((prev) => ({
      ...prev,
      [newMember.id]: { signed: false, signerRelationship: null },
    }));
    setSelectedFamilyMember(newMember);
    setShowAddFamilyPopup(false);
    setAddFamilyLoading(false);
    showToast('Family member added!');

    // Prompt for HIPAA signing (same flow for child and spouse)
    setShowFamilyHipaaPrompt(newMember);
  };

  /* ---------------------------
     FILE HANDLING
  ---------------------------- */
  const handleFile = useCallback(async (picked: File) => {
    if (!isAcceptedFile(picked)) {
      phFailure({
        step: 'bill_file_rejected',
        message: 'unsupported_type',
        context: { file_type: picked.type || 'none', file_ext: fileExt(picked) },
      });
      setError("We can't read that kind of file. A PDF or a photo of the bill works.");
      return;
    }

    // HEIC to JPEG where the browser can decode it, so it previews here and
    // in the admin. Browsers that can't get the original back.
    const selected = await prepareImageForUpload(picked);

    if (selected.size > MAX_FILE_SIZE) {
      // A big photo gets shrunk before upload anyway, so only PDFs are
      // really held to the limit.
      const shrunk = selected.type.startsWith('image/') ? await shrinkImage(selected) : selected;
      if (shrunk.size > MAX_FILE_SIZE) {
        phFailure({
          step: 'bill_file_rejected',
          message: 'too_large',
          context: { file_type: selected.type || 'none', file_size: selected.size },
        });
        setError('That file is over 10 MB. Try a photo of the bill, or a smaller PDF.');
        return;
      }
    }

    setError(null);
    setFile(selected);

    if (selected.type === 'image/jpeg' || selected.type === 'image/png' || selected.type === 'image/webp') {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target?.result as string);
      reader.readAsDataURL(selected);
    } else {
      setFilePreview(null);
    }
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDragging) setIsDragging(true);
    },
    [isDragging],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) void handleFile(dropped);
    },
    [handleFile],
  );

  /* ---------------------------
     EOB FILE HANDLING
  ---------------------------- */
  const handleEobFile = useCallback(async (picked: File) => {
    if (!isAcceptedFile(picked)) {
      phFailure({
        step: 'eob_file_rejected',
        message: 'unsupported_type',
        context: { file_type: picked.type || 'none', file_ext: fileExt(picked) },
      });
      setError("We can't read that kind of file. A PDF or a photo of your EOB works.");
      return;
    }
    const selected = await prepareImageForUpload(picked);
    if (selected.size > MAX_FILE_SIZE) {
      phFailure({ step: 'eob_file_rejected', message: 'too_large' });
      setError('EOB file must be under 10 MB.');
      return;
    }
    setError(null);
    setEobFile(selected);
  }, []);

  /* ---------------------------
     FREE ACCOUNT UPLOAD (2026-10-05)
     Puts the file in storage and parks it in bill_drafts. No bills row,
     no email to the team, nobody starts work. Then the pay popup.
  ---------------------------- */
  const handleFreeUpload = async () => {
    if (!user) {
      setError('You must be logged in.');
      return;
    }

    // Came back to an upload that's already parked and didn't pick a new file.
    if (!file) {
      setPaywallTrigger('waiting_card');
      setShowPaywall(true);
      return;
    }

    setLoading(true);
    setError(null);

    const ts = Date.now();
    // Photos get shrunk first, see lib/shrinkImage.ts.
    const upload = await shrinkImage(file);
    const filePath = `${user.id}/${ts}-${upload.name}`;
    const { error: uploadError } = await supabase.storage
      .from('medical_bills')
      .upload(filePath, upload);

    if (uploadError) {
      phFailure({
        step: 'free_bill_upload_storage',
        message: uploadError.message,
        context: { file_type: file.type, file_size: file.size },
      });
      setError(uploadError.message);
      setLoading(false);
      return;
    }

    // More pages of the same bill. One that fails to upload is dropped
    // with a note, the rest still go through.
    const extras: { path: string; name: string }[] = [];
    for (let i = 0; i < extraFiles.length; i += 1) {
      const page = await shrinkImage(extraFiles[i]);
      const pagePath = `${user.id}/${ts}-p${i + 2}-${page.name}`;
      const { error: pageError } = await supabase.storage.from('medical_bills').upload(pagePath, page);
      if (pageError) {
        phFailure({ step: 'free_bill_page_upload', message: pageError.message });
        showToast(`${page.name} didn't upload, the rest did.`, 'error');
      } else {
        extras.push({ path: pagePath, name: page.name });
      }
    }

    let eobPath: string | null = null;
    if (eobFile) {
      const eobFilePath = `eob/${user.id}/${ts}-${eobFile.name}`;
      const { error: eobUploadError } = await supabase.storage
        .from('medical_bills')
        .upload(eobFilePath, eobFile);
      if (!eobUploadError) {
        eobPath = eobFilePath;
      } else {
        phFailure({
          step: 'free_eob_attach_upload',
          message: eobUploadError.message,
          context: { file_type: eobFile.type, file_size: eobFile.size },
        });
      }
    }

    const next = {
      user_id: user.id,
      file_path: filePath,
      file_name: upload.name,
      eob_path: eobPath,
      extra_files: extras,
      provider_name: providerName.trim() || null,
      comment: comment.trim() || null,
      due_date: dueDate || null,
    };
    const { id: draftId, error: saveError } = await saveBillDraft(next, user.email);

    if (saveError) {
      phFailure({ step: 'free_bill_draft_save', message: saveError });
      showToast("We couldn't save your bill. Please try again.", 'error');
      setLoading(false);
      return;
    }

    phFreeBillUploaded();

    // On to the case page, which runs the first look and offers the pay
    // step from there.
    setLoading(false);
    navigate(draftId ? `/case/draft?id=${draftId}` : '/case/draft', { replace: true });
  };

  const addExtraPages = async (list: FileList | null) => {
    if (!list) return;
    const all = Array.from(list);
    const ok = all.filter(isAcceptedFile);
    const prepared = await Promise.all(ok.map((f) => prepareImageForUpload(f)));
    const picked = prepared.filter((f) => f.type.startsWith('image/') || f.size <= MAX_FILE_SIZE);
    if (picked.length < all.length) {
      setError('Some pages were skipped. Use PDFs or photos, and PDFs under 10 MB.');
    }
    setExtraFiles((prev) => [...prev, ...picked].slice(0, MAX_EXTRA_PAGES));
  };

  /* ---------------------------
     SUBMIT
  ---------------------------- */
  const handleSubmit = async () => {
    setError(null);

    if (outOfBills) {
      setError('You have used all bills for this period.');
      return;
    }

    if (!file && !draft) {
      setError("Attach your itemized bill above and we'll get started.");
      uploadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (isFree) {
      await handleFreeUpload();
      return;
    }

    if (isForFamilyMember && !selectedFamilyMember) {
      setError('Please select a family member for this bill.');
      return;
    }

    if (selectedFamilyMember) {
      const status = hipaaStatus[selectedFamilyMember.id];
      const hasSigned = status?.signed ?? false;
      const agedOut =
        hasSigned &&
        status?.signerRelationship === 'parent' &&
        !isMinorForHipaa(selectedFamilyMember.date_of_birth);

      if (!hasSigned || agedOut) {
        setError(
          agedOut
            ? `${selectedFamilyMember.first_name} has turned 18 and needs to sign their own HIPAA authorization.`
            : 'HIPAA authorization is required for this family member before uploading a bill.'
        );
        return;
      }
    }

    setLoading(true);
    setError(null);

    if (!user) {
      setError('You must be logged in.');
      setLoading(false);
      return;
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('date_of_birth, phone_e164')
      .eq('id', user.id)
      .single();

    if (!profile?.date_of_birth || !profile?.phone_e164) {
      navigate('/onboarding');
      setLoading(false);
      return;
    }

    const ts = Date.now();
    // A bill uploaded before paying is already in storage, so reuse it
    // unless they picked a different file just now.
    let filePath = draft?.file_path ?? '';
    const fileName = file?.name ?? draft?.file_name ?? 'Bill';

    if (file) {
      const upload = await shrinkImage(file);
      filePath = `${user.id}/${ts}-${upload.name}`;

      const { error: uploadError } = await supabase.storage
        .from('medical_bills')
        .upload(filePath, upload);

      if (uploadError) {
        /* 2026-08-08: this returned silently as far as analytics went. Bill
           upload is the step that gates the entire product and there was no
           event for it failing, so upload success rate was not measurable. */
        phFailure({
          step: 'bill_upload_storage',
          message: uploadError.message,
          context: { file_type: file.type, file_size: file.size },
        });
        setError(uploadError.message);
        setLoading(false);
        return;
      }
    }

    // Upload optional EOB if provided, or reuse the one from an EOB-upgrade
    // flow, or the one that came with the pre-payment upload.
    let eobPath: string | null = eobUpgradePath ?? draft?.eob_path ?? null;
    if (eobFile) {
      const eobFilePath = `eob/${user.id}/${ts}-${eobFile.name}`;
      const { error: eobUploadError } = await supabase.storage
        .from('medical_bills')
        .upload(eobFilePath, eobFile);
      if (!eobUploadError) {
        eobPath = eobFilePath;
      } else {
        /* 2026-08-08: this failure was swallowed completely — no else branch,
           no event, and no user-visible error. The customer attached an EOB,
           it silently did not upload, and the bill went through without it. */
        phFailure({
          step: 'eob_attach_upload',
          message: eobUploadError.message,
          context: { file_type: eobFile.type, file_size: eobFile.size },
        });
      }
    }

    const billId = crypto.randomUUID();

    // The due date from a pre-payment upload has no column on bills, so it
    // rides in the note where the specialist will read it.
    const commentWithDueDate = draft?.due_date
      ? [comment.trim(), `Bill due date (from the customer): ${draft.due_date}`].filter(Boolean).join('\n')
      : comment;

    const { error: billError } = await supabase.from('bills').insert({
      id: billId,
      user_id: user.id,
      family_member_id: selectedFamilyMember?.id || null,
      file_path: filePath,
      eob_path: eobPath,
      patient_email: user.email,
      patient_dob: profile.date_of_birth,
      patient_phone: profile.phone_e164,
      authorization_signed: true,
      status: 'submitted',
      comment: commentWithDueDate || null,
      display_name: providerName.trim() || fileName,
      provider_name: providerName.trim() || null,
    });

    if (billError) {
      phFailure({
        step: 'bill_insert_db',
        message: billError.message,
        code: billError.code,
        context: { bill_id: billId },
      });
      showToast('Bill upload failed, please try again', 'error');
      setLoading(false);
      return;
    }

    // The parked upload is a real case now. The files stay where they
    // are, the bills row points at them.
    if (draft) {
      // Extra pages become attachments on the case, same shape the case
      // page uses. Skipped if they swapped in a different file just now.
      if (!file && draft.extra_files.length > 0) {
        const pages = draft.extra_files.map((f) => ({
          url: supabase.storage.from('medical_bills').getPublicUrl(f.path).data.publicUrl,
          name: f.name,
          type: f.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg',
          uploaded_at: new Date().toISOString(),
        }));
        const { error: pagesError } = await supabase.rpc('update_bill_attachments', {
          bill_id: billId,
          new_attachments: pages,
          message: null,
        });
        if (pagesError) {
          phFailure({ step: 'draft_pages_attach', message: pagesError.message, context: { bill_id: billId } });
        }
      }
      // Only this one. Any other bills they parked stay where they are.
      await deleteBillDraft(draft, false);
      clearPaidDraftId();
      setDraft(null);
    }

    // Fire submission confirmation email (fire-and-forget)
    supabase.auth.getSession().then(({ data }) => {
      const token = data.session?.access_token;
      if (token) {
        fetch(`${API_BASE}/notify/bill-submitted`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ billId }),
        }).catch(() => {/* non-fatal */});
      }
    });

    // Show success state with fox-cape before navigating
    setLoading(false);
    setSubmitSuccess(true);

    // Clear EOB upgrade carry-over if used
    localStorage.removeItem('eob_upgrade_path');

    showToast('Bill submitted successfully!');
    trackBillUploaded({ provider: providerName.trim() || undefined });
    /* ⚠️ 2026-08-08 — THE ACTIVATION EVENT, now segmentable.
       This is the strongest retention predictor mediloop has (see the Meta
       comment below, which already says so), and it used to carry only
       `provider` and `for_family_member`. That made the two questions that
       actually matter unanswerable: "is this their FIRST bill?" (the
       activation cohort) and "did this bill go on to produce savings?" (needs
       bill_id to join against verdict_viewed). `billsUsed` was already in
       scope at this call site and simply wasn't used. */
    phBillSubmitted({
      provider: providerName.trim() || undefined,
      forFamilyMember: !!selectedFamilyMember,
      billId,
      isFirstBill: billsUsed === 0,
      billOrdinal: billsUsed + 1,
      hasEob: Boolean(eobPath),
      hasDescription: Boolean(comment && comment.trim()),
      fileType: file?.type || undefined,
    });
    updateIntercom({ bills_used: '+1' });
    // bills_used just changed — keep the PostHog person profile as current as
    // Intercom's, or cohorts keyed on bills_used read stale for the session.
    refreshPostHogIdentity();
    // Meta: custom event so we can build lookalikes off engaged users.
    // Bill upload is the strongest product-engagement signal Mediloop has  
    // someone who uploads a bill is dramatically more likely to renew/upgrade
    // than someone who just paid. event_id = billId for cross-source dedup.
    void trackCustomEvent({
      name: 'BillUploaded',
      eventID: billId,
      customData: {
        provider: providerName.trim() || null,
        for_family_member: !!selectedFamilyMember,
      },
      email: user.email || undefined,
    });

    // Navigate after a brief celebration
    setTimeout(() => navigate('/'), 2500);
  };

  /* ---------------------------
     HELPERS
  ---------------------------- */
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /* ---------------------------
     HIPAA prompt message (differs for child vs spouse)
  ---------------------------- */
  const getHipaaPromptMessage = () => {
    if (!showFamilyHipaaPrompt) return '';
    const name = showFamilyHipaaPrompt.first_name;
    const isSpouse = showFamilyHipaaPrompt.relationship === 'spouse';
    const isAdultChild =
      showFamilyHipaaPrompt.relationship === 'child' &&
      !isMinorForHipaa(showFamilyHipaaPrompt.date_of_birth);

    if (isSpouse || isAdultChild) {
      // Adult family members sign for themselves via device handoff
      return `${name} needs to sign their own HIPAA authorization. Hand them your device, it only takes 30 seconds.`;
    }
    // Minor children, parent signs on their behalf
    return `Before you can upload a bill for ${name}, you need to sign a HIPAA authorization on their behalf. This only takes a minute.`;
  };

  /* ---------------------------
     UI, MAIN
  ---------------------------- */
  // Refunded customers cannot submit new bills
  if (subscription?.status === 'refunded') {
    return (
      <div className="add-bill-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '32px 24px' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>💸</div>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#0E1530', marginBottom: '8px' }}>
            Your account has been refunded
          </h2>
          <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6', marginBottom: '20px' }}>
            You can&apos;t submit new bills on a refunded account. If you&apos;d like to continue using Mediloop, please get in touch.
          </p>
          <a
            href="mailto:help@mediloop.ai"
            style={{
              display: 'inline-block',
              background: 'linear-gradient(to right, #E97A50, #C8542A)',
              color: '#fff',
              padding: '10px 24px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            Contact Us
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="add-bill-container">
      <StartCasePaywall
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        email={user?.email}
        hasDraft={Boolean(draft)}
        draftId={draft?.id ?? null}
        fileName={draft?.file_name ?? null}
        dueDate={draft?.due_date ?? null}
        trigger={paywallTrigger}
      />

      <ExampleBillModal open={showExample} onClose={() => setShowExample(false)} />

      {/* Full-screen upload overlay */}
      {loading && (
        <div className="upload-overlay">
          <img
            src="/images/mascot/fox-reading-bill.png"
            alt=""
            className="upload-overlay-fox"
          />
          <p className="upload-overlay-msg">
            {isFree ? 'Uploading your bill…' : 'Sending your bill…'}
          </p>
          <div className="upload-overlay-dots">
            <span /><span /><span />
          </div>
        </div>
      )}

      {/* Success overlay */}
      {submitSuccess && (
        <div className="upload-overlay success">
          <img
            src="/images/mascot/fox-cape.png"
            alt="Case filed!"
            className="upload-overlay-fox"
            style={{ animation: 'none' }}
          />
          <h2 className="upload-success-title">Your case is open</h2>
          <p className="upload-overlay-msg">
            A mediloop specialist will message you in the case as soon as
            they've been through your bill.
          </p>
          <button
            className="upload-success-btn"
            onClick={() => navigate('/')}
          >
            Go to my cases
          </button>
        </div>
      )}

      <main className="add-bill-card">
        {pageLoading ? (
          <div
            className="limit-card"
            style={{ boxShadow: 'none', background: 'transparent' }}
          >
            <img
              src="/images/mascot/fox-cape.png"
              alt=""
              style={{
                width: 110,
                height: 'auto',
                filter: 'drop-shadow(0 4px 12px rgba(14, 21, 48, 0.12))',
                animation: 'foxRun 1.6s ease-in-out infinite',
              }}
            />
            <p style={{ color: 'var(--color-text-muted)', marginTop: 8 }}>Loading…</p>
          </div>
        ) : outOfBills ? (
          <div className="limit-card">
            {/* Out of bills shouldn't trap anyone. Closing takes them back to
                where they were so they can keep using everything else. */}
            <button type="button" className="limit-close" aria-label="Close" onClick={leaveLimitCard}>
              <X size={18} />
            </button>
            <div className="limit-icon">
              <Lock size={28} />
            </div>

            {/* Oct 2026: this used to say "You've reached your limit" with an
                "Upgrade plan" button that went to /plan. People had to find
                the add-a-bill option there, pay, then come back here. Now
                they pay from here and Stripe brings them back to the form. */}
            {isAnnualPlan ? (
              <>
                <h2>You've used all {subscription?.bills_limit} bills this plan year</h2>
                <p>
                  Have another one? Add it for <strong>${addonPrice}</strong> and send it straight
                  over. Your 6 bills start again when the plan renews.
                </p>
              </>
            ) : (
              <>
                <h2>Start another case</h2>
                <p>
                  Your plan covered {subscription?.bills_limit === 1 ? 'one bill' : `${subscription?.bills_limit} bills`}.
                  A new bill is <strong>${addonPrice}</strong>, refunded in full if we
                  don't lower it by at least ${addonPrice}.
                </p>
              </>
            )}

            <button className="primary" onClick={handleBuyAnotherBill} disabled={addonLoading}>
              {addonLoading ? 'Opening checkout…' : `Pay $${addonPrice} and add the bill`}
            </button>

            {!isAnnualPlan && (
              <p className="limit-alt">
                Got a few bills?{' '}
                <button type="button" className="limit-alt-link" onClick={() => navigate('/plan')}>
                  The Annual Plan covers 6 a year
                </button>
                , with the ${addonPrice} you paid taken off.
              </p>
            )}

            <button
              type="button"
              className="limit-back"
              onClick={leaveLimitCard}
            >
              Not now
            </button>
          </div>
        ) : (
          <>
            {/* Oct 2026: the intro reads as one message from us, the way
                Granted opens its upload: what we check for, an example,
                and a way out if the bill isn't to hand. */}
            <div className="ab-intro">
              <img src="/images/mascot/fox-avatar.png" alt="" className="ab-intro-avatar" />
              <div className="ab-intro-body">
                <h1>Upload your bill and we'll check it for:</h1>
                <ol className="ab-checks">
                  <li>Charges billed more than once</li>
                  <li>Codes that don't match the care you got</li>
                  <li>Insurance that wasn't applied correctly</li>
                  <li>Prices well above published rates</li>
                </ol>
                <p className="add-bill-sub">
                  {isFree
                    ? 'Seeing what we find is free. '
                    : 'Our AI reads it first, then a mediloop specialist checks what it found and takes it from there. '}
                  <button type="button" className="ml-link" onClick={() => setShowExample(true)}>
                    See an example bill
                  </button>
                </p>
                {!isFree && (
                  <p className="ab-nobill">
                    Don't have the bill with you?{' '}
                    <button type="button" className="ml-link" onClick={() => showIntercom()}>
                      Message us
                    </button>{' '}
                    and tell us what's going on.
                  </p>
                )}
              </div>
            </div>

            {/* Recipient banner, shows when a family member is selected */}
            {selectedFamilyMember && (
              <div className="recipient-banner">
                <div className="recipient-banner-info">
                  {selectedFamilyMember.relationship === 'child' ? (
                    <Baby size={16} />
                  ) : (
                    <Heart size={16} />
                  )}
                  <span>
                    This bill is for{' '}
                    <strong>
                      {selectedFamilyMember.first_name} {selectedFamilyMember.last_name}
                    </strong>
                    <span className={`recipient-banner-badge ${selectedFamilyMember.relationship}`}>
                      {selectedFamilyMember.relationship === 'child' ? 'Child' : 'Spouse'}
                    </span>
                  </span>
                </div>
                <button
                  type="button"
                  className="recipient-banner-change"
                  onClick={() => setShowFamilyPickerPopup(true)}
                >
                  Change
                </button>
              </div>
            )}

            <div className="add-bill-grid">
            <div className="add-bill-left">
            <div className="ab-card-head">
              <p className="ab-card-title">Your bill</p>
              <p className="ab-card-sub">A PDF, or a photo of the paper bill.</p>
            </div>
            {/* Upload */}
            {!file && draft ? (
              <div className="file-preview-card" ref={uploadRef}>
                <div className="file-preview-left">
                  <div className="file-icon-box">
                    <FileText size={24} />
                  </div>
                </div>
                <div className="file-preview-info">
                  <span className="file-preview-name">{draft.file_name || 'Your bill'}</span>
                  <span className="file-preview-size">Uploaded earlier, ready to send</span>
                </div>
                <button
                  type="button"
                  className="file-change-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Change
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept={ACCEPT_ATTR}
                  onChange={(e) => {
                    if (e.target.files?.[0]) void handleFile(e.target.files[0]);
                  }}
                />
              </div>
            ) : !file ? (
              <div
                ref={uploadRef}
                className={`drop-zone ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept={ACCEPT_ATTR}
                  onChange={(e) => {
                    if (e.target.files?.[0]) void handleFile(e.target.files[0]);
                  }}
                />
                <UploadCloud size={34} strokeWidth={1.5} className="drop-zone-icon" aria-hidden />
                <strong>
                  {isTouch ? 'Tap to add your bill' : 'Drop your bill here, or click to choose it'}
                </strong>
                <span className="drop-zone-hint">
                  {isTouch ? 'Take a photo of it, or pick a PDF or photo you already have' : 'PDF, JPG, HEIC or PNG'}
                </span>
                {/* Clicks bubble up to the drop zone, which opens the picker. */}
                <span className="ml-btn ml-btn--ink drop-zone-btn" role="presentation">
                  <UploadCloud size={15} /> Select file
                </span>

              </div>
            ) : (
              <div className="file-preview-card">
                <div className="file-preview-left">
                  {filePreview ? (
                    <img
                      src={filePreview}
                      alt="Bill preview"
                      className="file-thumbnail"
                    />
                  ) : (
                    <div className="file-icon-box">
                      <FileText size={24} />
                    </div>
                  )}
                </div>
                <div className="file-preview-info">
                  <span className="file-preview-name">{file.name}</span>
                  <span className="file-preview-size">
                    {formatFileSize(file.size)}
                  </span>
                </div>
                <button
                  type="button"
                  className="file-change-btn"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Change
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept={ACCEPT_ATTR}
                  onChange={(e) => {
                    if (e.target.files?.[0]) void handleFile(e.target.files[0]);
                  }}
                />
              </div>
            )}

            {isFree && (
              <div className="free-upload-extras">
                {!file && isTouch && (
                  <>
                    <button
                      type="button"
                      className="free-camera-btn"
                      onClick={() => cameraInputRef.current?.click()}
                    >
                      <Camera size={16} /> Take a photo
                    </button>
                    <input
                      ref={cameraInputRef}
                      type="file"
                      hidden
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => {
                        if (e.target.files?.[0]) void handleFile(e.target.files[0]);
                        e.target.value = '';
                      }}
                    />
                  </>
                )}

                {file && (
                  <div className="free-pages">
                    {extraFiles.map((page, i) => (
                      <div className="eob-file-row" key={`${page.name}-${i}`}>
                        <FileText size={16} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                        <span className="eob-file-name">{page.name}</span>
                        <button
                          type="button"
                          className="eob-file-remove"
                          onClick={() => setExtraFiles((prev) => prev.filter((_, j) => j !== i))}
                          aria-label={`Remove ${page.name}`}
                        >
                          <XIcon size={14} />
                        </button>
                      </div>
                    ))}
                    {extraFiles.length < MAX_EXTRA_PAGES && (
                      <button
                        type="button"
                        className="free-add-page"
                        onClick={() => extraInputRef.current?.click()}
                      >
                        <Plus size={15} /> Add another page
                      </button>
                    )}
                    <input
                      ref={extraInputRef}
                      type="file"
                      hidden
                      multiple
                      accept={ACCEPT_ATTR}
                      onChange={(e) => {
                        void addExtraPages(e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </div>
                )}

                <p className="free-privacy">
                  <Lock size={13} /> Only you and your mediloop specialist can see what you upload.
                </p>

                {!file && (
                  <p className="free-later">
                    Bill not with you? No rush. Your account is saved, and we'll email you a link
                    that brings you straight back here.
                  </p>
                )}
              </div>
            )}

            {hasSelfHipaa && (
              <div className="family-toggle">
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={isForFamilyMember}
                    onChange={(e) => handleFamilyCheckboxChange(e.target.checked)}
                  />
                  <span>This bill is for a family member.</span>
                </label>
                <div className="tooltip-wrapper">
                  <span
                    className="tooltip-trigger"
                    aria-label="More info about family member bills"
                    tabIndex={0}
                  >
                    <Info size={15} />
                  </span>
                  <div className="tooltip-popover">
                    <div className="tooltip-arrow" />
                    If this bill is for a child or spouse, we'll need a separate HIPAA
                    authorization for them before we can work on their behalf.
                  </div>
                </div>
              </div>
            )}

            {/* Optional context, collapsed so the default view stays a clean
                upload and send. Provider, a note, and an EOB all live here. */}
            <details className="details-disclosure">
              <summary>
                <span className="details-plus">+</span>
                <span>{isFree ? 'Add the due date, provider, a note or an EOB' : 'Add provider, a note, or an EOB'}</span>
                <span className="details-optional">optional</span>
              </summary>
              <div className="details-body">
                <div className="form-fields">
                  {isFree && (
                    <div className="simple-field">
                      <label htmlFor="dueDate">
                        When is it due? <span className="optional-tag">optional</span>
                      </label>
                      <input
                        type="date"
                        id="dueDate"
                        className="simple-input"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                      />
                    </div>
                  )}
                  <div className="simple-field">
                    <label htmlFor="providerName">Where's this bill from? <span className="optional-tag">optional</span></label>
                    <input
                      type="text"
                      id="providerName"
                      className="simple-input"
                      value={providerName}
                      onChange={(e) => setProviderName(e.target.value)}
                      placeholder="e.g. Mount Sinai, Quest Diagnostics"
                    />
                  </div>

                  <div className="simple-field">
                    <label htmlFor="comment">Anything else we should know? <span className="optional-tag">optional</span></label>
                    <textarea
                      id="comment"
                      className="simple-input"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      rows={2}
                      placeholder='e.g. "I already called the provider," "This was an emergency visit," "I have insurance"'
                    />
                  </div>
                </div>

                <div className="eob-attach-section">
                  <button
                    type="button"
                    className="eob-attach-toggle"
                    onClick={() => {
                      if (!eobUpgradePath) {
                        setShowEobUpload((v) => !v);
                        if (showEobUpload) setEobFile(null);
                      }
                    }}
                    style={eobUpgradePath ? { cursor: 'default' } : undefined}
                  >
                    <Paperclip size={14} />
                    <span>Have an EOB for this bill?</span>
                    <span className="optional-tag">optional</span>
                    {!eobUpgradePath && (showEobUpload ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                  </button>

                  {showEobUpload && (
                    <div className="eob-attach-body">
                      {eobUpgradePath && !eobFile ? (
                        <div className="eob-file-row" style={{ background: 'rgba(93,171,148,0.08)', border: '1px solid rgba(93,171,148,0.3)', borderRadius: 8, padding: '10px 12px' }}>
                          <FileText size={16} style={{ color: '#169A6F', flexShrink: 0 }} />
                          <span className="eob-file-name" style={{ color: '#0F6E56', fontWeight: 600 }}>
                            EOB from your review, already attached
                          </span>
                          <button
                            type="button"
                            className="eob-file-remove"
                            onClick={() => { setEobUpgradePath(null); localStorage.removeItem('eob_upgrade_path'); }}
                            aria-label="Remove EOB"
                          >
                            <XIcon size={14} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <p className="eob-attach-hint">
                            An Explanation of Benefits helps us spot problems faster, so upload yours if you have it.
                          </p>
                          {!eobFile ? (
                            <div
                              className={`eob-drop-zone ${isDraggingEob ? 'dragging' : ''}`}
                              onDragOver={(e) => { e.preventDefault(); setIsDraggingEob(true); }}
                              onDragLeave={() => setIsDraggingEob(false)}
                              onDrop={(e) => { e.preventDefault(); setIsDraggingEob(false); const f = e.dataTransfer.files[0]; if (f) void handleEobFile(f); }}
                              onClick={() => eobInputRef.current?.click()}
                            >
                              <input
                                ref={eobInputRef}
                                type="file"
                                hidden
                                accept={ACCEPT_ATTR}
                                onChange={(e) => { if (e.target.files?.[0]) void handleEobFile(e.target.files[0]); }}
                              />
                              <FileText size={20} style={{ color: 'var(--color-primary)', marginBottom: 6 }} />
                              <span>Upload your EOB (a PDF or a photo)</span>
                            </div>
                          ) : (
                            <div className="eob-file-row">
                              <FileText size={16} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                              <span className="eob-file-name">{eobFile.name}</span>
                              <button
                                type="button"
                                className="eob-file-remove"
                                onClick={() => { setEobFile(null); if (eobInputRef.current) eobInputRef.current.value = ''; }}
                                aria-label="Remove EOB"
                              >
                                <XIcon size={14} />
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </details>

            {error && <p className="error">{error}</p>}

            {!loading && !file && !draft && !isFree && (
              <p className="submit-hint">
                {isFree ? 'Attach your itemized bill above.' : 'Attach your itemized bill above to send.'}
              </p>
            )}

            {/* Free accounts: the drop zone is the ask until a file is picked,
                then this is the one button. No Cancel, there's nowhere
                better to send them. */}
            {(!isFree || file) && (
            <div className={`actions-row${isFree ? ' actions-row--free' : ''}`}>
              <button
                className="primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (isFree ? 'Uploading...' : 'Sending...') : isFree ? 'Get my free first look' : 'Send my bill'}
              </button>
              {!isFree && (
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  if (hasUnsavedChanges) {
                    setShowCancelConfirm(true);
                  } else {
                    navigate('/');
                  }
                }}
                disabled={loading}
              >
                Cancel
              </button>
              )}
            </div>
            )}

            {!isFree && (() => {
              const accountTag = subscription?.short_id
                ? `case account ${subscription.short_id}`
                : `${user?.email ?? 'my account'}`;
              const subject = encodeURIComponent(`Collections refund - ${accountTag}`);
              const body = encodeURIComponent(
                `Hi mediloop team,\n\n` +
                `I just opened a case but my bill is already in collections, so you can't take this one on. ` +
                `Could you refund my case fee?\n\n` +
                `Account: ${user?.email ?? '(your email)'}\n` +
                (subscription?.short_id ? `Subscription: ${subscription.short_id}\n` : '') +
                `\nThank you.`,
              );
              const mailto = `mailto:help@mediloop.ai?subject=${subject}&body=${body}`;
              return (
                <details className="eligibility-note">
                  <summary>
                    We work on bills under 2 years old that aren't in collections
                    or part of a lawsuit.
                  </summary>
                  <div className="eligibility-note-body">
                    <p>
                      If your bill is in collections or legal status the rules change:
                      providers no longer own the debt, so we can't negotiate
                      it down.
                    </p>
                    <p>
                      <strong>You won't pay for it.</strong>{' '}
                      <a href={mailto}>Email us</a> and we'll refund your case fee.
                    </p>
                  </div>
                </details>
              );
            })()}

            </div>{/* /.add-bill-left */}
            </div>{/* /.add-bill-grid */}

          </>
        )}
      </main>

      {/* ---- HIPAA PROMPT (no self-HIPAA): "Who is this bill for?" ---- */}
      <Modal
        open={showHipaaPrompt}
        /* Dismiss in place. Do NOT navigate to /dashboard here:
           DashboardRedirect sends customers with no bills straight back
           to /add-bill, which re-opens this prompt in a loop. Upload is
           independently gated on hasSelfHipaa below, so closing is safe. */
        onClose={() => setShowHipaaPrompt(false)}
        className="bill-for-popup"
        priority
        showClose
        closeLabel="Close"
      >
        <h3>Authorization required</h3>
        <p>
          Before you can upload a bill, we need a HIPAA authorization.
          This allows us to access medical billing information and negotiate
          on your behalf. Who is this bill for?
        </p>
        <div className="bill-for-options">
          <button
            type="button"
            className="bill-for-option"
            onClick={handleHipaaForMe}
          >
            <UserCheck size={20} />
            <div className="bill-for-option-text">
              <strong>It's for me</strong>
              <span>I'll sign my own HIPAA authorization</span>
            </div>
          </button>
          <button
            type="button"
            className="bill-for-option"
            onClick={handleHipaaForFamily}
          >
            <Heart size={20} />
            <div className="bill-for-option-text">
              <strong>It's for a family member</strong>
              <span>A child or spouse</span>
            </div>
          </button>
        </div>
      </Modal>

      {/* ---- Family picker popup ---- */}
      <Modal
        open={showFamilyPickerPopup}
        onClose={() => {
          setShowFamilyPickerPopup(false);
          if (!hasSelfHipaa) {
            setShowHipaaPrompt(true);
          } else if (!selectedFamilyMember) {
            setIsForFamilyMember(false);
          }
        }}
        className="family-picker-popup"
      >
        <h3>Select a family member</h3>
        <p>Choose who this bill is for, or add someone new.</p>

        {familyMembers.length > 0 && (
          <div className="family-picker-list">
            {familyMembers.map((member) => {
              const age = calculateAge(member.date_of_birth);
              const status = hipaaStatus[member.id];
              const hasSigned = status?.signed ?? false;
              const agedOut =
                hasSigned &&
                status?.signerRelationship === 'parent' &&
                !isMinorForHipaa(member.date_of_birth);
              const needsHipaa = !hasSigned || agedOut;
              const Icon = member.relationship === 'child' ? Baby : Heart;

              return (
                <button
                  key={member.id}
                  type="button"
                  className="family-picker-item"
                  onClick={() => handleSelectFamilyMember(member)}
                >
                  <Icon size={18} />
                  <div className="family-picker-item-text">
                    <strong>{member.first_name} {member.last_name}</strong>
                    <span className="family-picker-item-meta">
                      <span className={`recipient-badge ${member.relationship}`}>
                        {member.relationship === 'child' ? 'Child' : 'Spouse'}
                      </span>
                      {member.relationship === 'child' && (
                        <span className="recipient-age">Age {age}</span>
                      )}
                      {needsHipaa && (
                        <span className="recipient-badge needs-hipaa">
                          {agedOut ? 'Re-sign needed' : 'HIPAA needed'}
                        </span>
                      )}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <button
          type="button"
          className="family-picker-add-btn"
          onClick={() => {
            setShowFamilyPickerPopup(false);
            setShowAddFamilyPopup(true);
          }}
        >
          <UserPlus size={16} />
          Add a new family member
        </button>

        <button
          type="button"
          className="family-picker-back"
          onClick={() => {
            setShowFamilyPickerPopup(false);
            if (!hasSelfHipaa) {
              setShowHipaaPrompt(true);
            } else if (!selectedFamilyMember) {
              setIsForFamilyMember(false);
            }
          }}
        >
          <ArrowLeft size={14} />
          Back
        </button>
      </Modal>

      {/* ---- Add new family member form ---- */}
      <Modal
        open={showAddFamilyPopup}
        onClose={() => {
          setShowAddFamilyPopup(false);
          setShowFamilyPickerPopup(true);
          setAddFamilyError(null);
        }}
        className="add-family-popup"
      >
        <h3>Add family member</h3>
        <p>We'll need some details so we can create a HIPAA authorization for them.</p>
        <FamilyMemberForm
          onSubmit={handleAddFamilyMember}
          onCancel={() => {
            setShowAddFamilyPopup(false);
            setShowFamilyPickerPopup(true);
            setAddFamilyError(null);
          }}
          loading={addFamilyLoading}
          error={addFamilyError}
        />
      </Modal>

      {/* ---- Existing dialogs ---- */}
      <ConfirmDialog
        open={showCancelConfirm}
        title="Discard changes?"
        message="You have unsaved changes. Are you sure you want to leave?"
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        onConfirm={() => navigate('/')}
        onCancel={() => setShowCancelConfirm(false)}
        variant="danger"
      />

      <ConfirmDialog
        open={!!showFamilyHipaaPrompt}
        title="HIPAA authorization needed"
        message={getHipaaPromptMessage()}
        confirmLabel="Sign Now"
        cancelLabel="Go Back"
        onConfirm={() => {
          if (showFamilyHipaaPrompt) {
            const isSpouse = showFamilyHipaaPrompt.relationship === 'spouse';
            const isAdultChild =
              showFamilyHipaaPrompt.relationship === 'child' &&
              !isMinorForHipaa(showFamilyHipaaPrompt.date_of_birth);
            const needsHandoff = isSpouse || isAdultChild;
            const params = new URLSearchParams();
            if (needsHandoff) params.set('handoff', 'true');
            params.set('returnTo', '/add-bill');
            navigate(
              `/hipaa/family/${showFamilyHipaaPrompt.id}?${params.toString()}`
            );
          }
        }}
        onCancel={() => {
          setShowFamilyHipaaPrompt(null);
          setSelectedFamilyMember(null);
          if (!hasSelfHipaa) {
            setShowHipaaPrompt(true);
          } else {
            setIsForFamilyMember(false);
          }
        }}
      />
    </div>
  );
}
